import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link, Outlet } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import * as common from '../../commons/common';
import { useIsLoginState } from '../../components/IsLoginContext';
import useRoleBasedAccess from "../../commons/useRoleBasedAccess";
// import DESCOlogo from '../../images/DESCO.png';
import UNIlogo from '../../images/UNI_LOGO_BLUE_SVG.svg';

import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Button,
    Divider,
    Collapse,
    ListItemIcon,
    IconButton,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
// icons
import MenuIcon from '@mui/icons-material/Menu';
import ListItemButton from '@mui/material/ListItemButton';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { BorderRight } from '@mui/icons-material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import QrCodeIcon from '@mui/icons-material/QrCode';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import validationAuth from '../../validationAuth';
import OrgManagementDialog from './orgMng/OrgManagementDialog';

const GeneralManager = ({ children }) => {
    useRoleBasedAccess(['ADMIN']);

    const location = useLocation();
    const navigate = useNavigate();
    const { user, setIsLogin } = useIsLoginState();

    const [open, setOpen] = useState(true);
    const [openMenus, setOpenMenus] = useState(false); //서브메뉴 open
    const [selectedMenu, setSelectedMenu] = useState(null);

    const [orgOpen, setOrgOpen] = useState(false);

    const menuItems = [
        // { text: '조직 관리', path: 'org-management', icon: <AccountTreeOutlinedIcon /> },
        { text: '사용자 설정', path: 'member-management', icon: <PersonSearchIcon /> },
        { text: '시설관리', path: 'facility-management', icon: <InventoryOutlinedIcon /> },
        { text: '화면 설정', path: 'screen-management', icon: <DisplaySettingsIcon /> },
        { text: 'QR코드 관리', path: 'qr-management', icon: <QrCodeIcon /> },
    ];

    const ScrollToTop = () => {
        window.scrollTo(0, 0);
    };

    const handleDrawer = useCallback(() => {
        setOpen(prevState => !prevState);
        setOpenMenus(prevState => !prevState);
    }, []);

    const handleMenuClick = useCallback((item) => {
        if (item.subItems) {
            setOpen(true);
            setOpenMenus(prevState => ({
                ...Object.fromEntries(Object.entries(prevState).map(([key]) => [key, false])),
                [item.text]: !prevState[item.text]
            }));
        } else {
            setSelectedMenu(item.text);
            setOpenMenus(prevState => ({
                ...Object.fromEntries(Object.entries(prevState).map(([key]) => [key, false]))
            }));
            if (item.path) {
                ScrollToTop();
                navigate(`/admin/${item.path}`);
            }
        }
    }, [navigate]);

    useEffect(() => {
        let activeMenu = menuItems.find(item => location.pathname.includes(item.path));

        if (activeMenu) {
            setSelectedMenu(activeMenu.text);
        }
    }, [location.pathname, menuItems]);

    const handleLogout = async () => {
        const url = `${common.getApiUrl()}/auth/logout`;
        const accessToken = localStorage.getItem('access_token');

        try {
            const response = await axios.post(url, {}, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            });
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setIsLogin(false);
            navigate('/', { replace: true });
        } catch (error) {
            common.handleApiError(error);
        }
    };

    const drawerWidth = 220;
    const drawerHeight = 65;

    const openedMixin = {
        width: drawerWidth,
        overflowX: 'hidden',
        borderRight: 0,
        display: 'flex',
        flexDirection: 'column',
    };

    const closedMixin = {
        overflowX: 'hidden',
        width: 56,
        display: 'flex',
        flexDirection: 'column',
    };

    const DrawerHeader = styled('div')({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '0 8px',
        minHeight: `${drawerHeight}px`,
        height: `${drawerHeight}px`,
        lineHeight: `${drawerHeight}px`,
        boxSizing: 'border-box',
        borderRight: '0',
    });

    const AppBar = styled(MuiAppBar, {
        shouldForwardProp: (prop) => prop !== 'open',
    })(({ open }) => ({
        zIndex: 1300,
        marginLeft: open ? drawerWidth : 56,
        lineHeight: `${drawerHeight}px`,
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: 'none',
        boxSizing: 'border-box',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        color: '#707070',
        width: `calc(100% - ${open ? drawerWidth : 56}px)`
    }));

    const Drawer = styled(MuiDrawer, {
        shouldForwardProp: (prop) => prop !== 'open',
    })(({ open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        border: 'none',
        ...(open && {
            ...openedMixin,
            '& .MuiDrawer-paper': openedMixin,
        }),
        ...(!open && {
            ...closedMixin,
            '& .MuiDrawer-paper': closedMixin,
        })
    }));

    return (
        <>
            <Helmet>
                <style>{'body { background-color: var(--main-white-color); }'}</style>
            </Helmet>
            <Box sx={{ width: '100%' }}>
                <AppBar position="fixed" open={open} >
                    <Toolbar sx={{ height: '65px', lineHeight: '65px', minHeight: '65px !important', boxSizing: 'border-box' }}>
                        <Typography variant='subtitle2' noWrap component="div" sx={{ color: '#000' }}>시스템 관리자</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                            <Typography variant="body2" sx={{ mr: 2 }} >
                                {user ? `${user.memberNm}님 안녕하세요` : '관리자님 안녕하세요'}
                            </Typography>
                            <Button color="inherit"
                                    sx={{ ml: 1, fontSize: '0.8rem' }}
                                    onClick={handleLogout}>
                                로그아웃
                            </Button>
                        </Box>
                    </Toolbar>
                </AppBar>

                <Drawer variant="permanent" open={open}>
                    <DrawerHeader>
                        <IconButton onClick={handleDrawer}>
                            <MenuIcon />
                        </IconButton>
                        <Box
                            component="img"
                            src={UNIlogo}
                            alt="KyungHeeUniversity Logo"
                            sx={{
                                maxWidth: '80px',
                                height: 'auto',
                                marginLeft: '10px',
                                cursor: 'pointer',
                            }}
                            onClick={() => navigate('/admin/member-management')}
                        />
                    </DrawerHeader>

                    <Divider />

                    <List sx={{ padding: '13px 0', flex: 1, overflowY: 'auto' }}>
                        {menuItems.map((item, index) => (
                            <ListItem key={index} disablePadding sx={{ display: 'block' }}>
                                <ListItemButton
                                    sx={{
                                        minHeight: 48,
                                        justifyContent: open ? 'initial' : 'center',
                                        color: selectedMenu === item.text ? 'var(--main-blue-color)' : null
                                    }}
                                    onClick={() => handleMenuClick(item)}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 1 : 'auto',
                                            color: selectedMenu === item.text ? 'var(--main-blue-color)' : null,
                                            justifyContent: 'left',
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText primary={item.text} sx={{ opacity: open ? 1 : 0 }} />
                                    {item.subItems && open ?
                                        (openMenus[item.text] ? <ExpandLess sx={{ color: '#CCCCCC' }} /> : <ExpandMore sx={{ color: 'var(--sub-darkgrey-color)' }} />) : null}
                                </ListItemButton>
                                {item.subItems && (
                                    <Collapse in={openMenus[item.text]} unmountOnExit={true}>
                                        <Box sx={{ mb: 2 }}>
                                            <List component="div" disablePadding>
                                                {item.subItems.map((subItem, subIndex) => (
                                                    <ListItemButton
                                                        key={subIndex}
                                                        sx={{
                                                            pl: 6,
                                                            height: '36px',
                                                            color: selectedMenu === subItem ? 'var(--main-blue-color)' : null
                                                        }}
                                                    >
                                                        <ListItemText
                                                            disableTypography
                                                            primary={subItem}
                                                            sx={{ fontSize: '0.9rem', fontFamily: 'Pretendard-Regular' }}
                                                        />
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        </Box>
                                    </Collapse>
                                )}
                            </ListItem>
                        ))}
                    </List>
                    {/* 하단 고정 – 조직관리 아이콘 */}
                    <Divider />
                    <List sx={{ padding: '4px 0' }}>
                        <ListItem disablePadding sx={{ display: 'block' }}>
                            <Tooltip title={open ? '' : '조직관리'} placement="right" arrow>
                                <ListItemButton
                                    onClick={() => setOrgOpen(true)}
                                    sx={{
                                        minHeight: 48,
                                        justifyContent: open ? 'initial' : 'center',
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 1 : 'auto',
                                            justifyContent: 'left',
                                            color: '#555',
                                        }}
                                    >
                                        <AccountTreeOutlinedIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="조직 관리" sx={{ opacity: open ? 1 : 0 }} />
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    </List>
                </Drawer>

                {/* 조직관리 팝업 */}
                <OrgManagementDialog open={orgOpen} onClose={() => setOrgOpen(false)} />

            </Box>
            <DrawerHeader />
            <Box sx={{ p: 3, marginLeft: open ? `${drawerWidth}px` : '56px'}}>
                <Outlet />
            </Box>
        </>
    );
};

export default validationAuth(GeneralManager);